// src/pages/Clients.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { formatDate, intervalLabel, intervalBadgeClass, getErrorMessage } from '../utils/helpers';
import { PageWrapper } from './Dashboard';

export default function Clients() {
  const navigate = useNavigate();
  const { isSupervisor } = useAuth();
  const [clients, setClients] = useState([]);
  const [discharged, setDischarged] = useState([]);
  const [beds, setBeds] = useState([]);
  const [dischargeTypes, setDischargeTypes] = useState([]);
  const [dischargeType, setDischargeType] = useState('');
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [showDischarged, setShowDischarged] = useState(false);
  const [intervalModal, setIntervalModal] = useState(null);
  const [roomModal, setRoomModal] = useState(null);
  const [roomHistory, setRoomHistory] = useState([]);
  const [locModal, setLocModal] = useState(null);
  const [locForm, setLocForm] = useState({ programType: 'residential', reason: '' });
  const [form, setForm] = useState({
    firstName: '', lastName: '', dateOfBirth: '', admitDate: '', admitTime: '',
    intervalMinutes: 15, roomNumber: '', notes: '', programType: 'residential'
  });
  const [intervalForm, setIntervalForm] = useState({ intervalMinutes: 15, reason: '' });
  const [roomForm, setRoomForm] = useState({ roomNumber: '', reason: '' });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [activeRes, dischargedRes, bedsRes, typesRes] = await Promise.all([
        api.get('/clients'),
        api.get('/clients/discharged'),
        api.get('/room-requests/beds').catch(() => ({ data: [] })),
        api.get('/discharge-requests/types').catch(() => ({ data: [] }))
      ]);
      setClients(activeRes.data);
      setDischarged(dischargedRes.data || []);
      setBeds(bedsRes.data || []);
      setDischargeTypes(typesRes.data || []);
    } catch {
      api.get('/clients').then(res => setClients(res.data));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // When program type changes to detox, enforce Q15/Q30 only
  const handleProgramTypeChange = (val) => {
    setForm(f => ({
      ...f,
      programType: val,
      intervalMinutes: val === 'detox' && ![15, 30].includes(parseInt(f.intervalMinutes)) ? 30 : f.intervalMinutes
    }));
  };

  const addClient = async (e) => {
    e.preventDefault(); setSaving(true); setError('');
    try {
      await api.post('/clients', { ...form, intervalMinutes: parseInt(form.intervalMinutes) });
      setShowAdd(false);
      setForm({ firstName: '', lastName: '', dateOfBirth: '', admitDate: '', admitTime: '', intervalMinutes: 15, roomNumber: '', notes: '', programType: 'residential' });
      load();
    } catch (err) { setError(getErrorMessage(err)); }
    finally { setSaving(false); }
  };

  const changeInterval = async (e) => {
    e.preventDefault(); setSaving(true); setError('');
    try {
      await api.patch(`/clients/${intervalModal.id}/interval`, { intervalMinutes: parseInt(intervalForm.intervalMinutes), reason: intervalForm.reason });
      setIntervalModal(null); load();
    } catch (err) { setError(getErrorMessage(err)); }
    finally { setSaving(false); }
  };

  const openRoomModal = async (client) => {
    setRoomModal(client);
    setRoomForm({ roomNumber: client.room_number || '', reason: '' });
    try { const res = await api.get(`/clients/${client.id}/room-history`); setRoomHistory(res.data); }
    catch { setRoomHistory([]); }
  };

  const changeRoom = async (e) => {
    e.preventDefault(); setSaving(true); setError('');
    try { await api.patch(`/clients/${roomModal.id}/room`, roomForm); setRoomModal(null); load(); }
    catch (err) { setError(getErrorMessage(err)); }
    finally { setSaving(false); }
  };

  const changeLoc = async (e) => {
    e.preventDefault(); setSaving(true); setError('');
    try {
      await api.patch(`/clients/${locModal.id}/program-type`, { programType: locForm.programType, reason: locForm.reason });
      setLocModal(null); load();
    } catch (err) { setError(getErrorMessage(err)); }
    finally { setSaving(false); }
  };

  const [dischargeModal, setDischargeModal] = useState(null);
  const [editModal, setEditModal] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [submittingEdit, setSubmittingEdit] = useState(false);
  const [dischargeReason, setDischargeReason] = useState('');
  const [dischargeTime, setDischargeTime] = useState('');
  const [submittingDischarge, setSubmittingDischarge] = useState(false);

  const openDischarge = (client) => {
    setDischargeReason(''); setDischargeType(''); setDischargeModal(client);
  };


  // Mirrors the server bounds in routes/clients.js. Client-side only stops the
  // obvious mistake early — the server is what actually enforces it.
  // Local dates, not UTC. toISOString() returns the UTC day, which in the
  // evening Pacific is already tomorrow — that would let the picker offer a
  // future admit date the server then rejects.
  const localDateStr = (offsetDays = 0) => {
    const x = new Date();
    x.setHours(12, 0, 0, 0);
    x.setDate(x.getDate() - offsetDays);
    return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
  };
  const todayStr = localDateStr(0);
  const daysAgoStr = (n) => localDateStr(n);


  // Beds come from the rooms table (18 across 10 rooms; 4 and 7 are singles),
  // not a hardcoded 10x2 grid, and show who is in them.
  const bedOptions = (currentValue) => {
    const open = beds.filter(b => !b.occupied);
    const taken = beds.filter(b => b.occupied && b.bed !== currentValue);
    return (
      <>
        <optgroup label={`Open (${open.length})`}>
          {open.map(b => <option key={b.bed} value={b.bed}>Room {b.room} — Bed {b.bed.slice(-1)}</option>)}
        </optgroup>
        {taken.length > 0 && (
          <optgroup label="Occupied">
            {taken.map(b => (
              <option key={b.bed} value={b.bed}>Room {b.room} — Bed {b.bed.slice(-1)} · {b.occupants.join(', ')}</option>
            ))}
          </optgroup>
        )}
      </>
    );
  };

  const openEdit = (client) => {
    setEditForm({
      firstName: client.first_name || '',
      lastName: client.last_name || '',
      dateOfBirth: client.date_of_birth ? client.date_of_birth.slice(0, 10) : '',
      admitDate: client.admit_date ? client.admit_date.slice(0, 10) : '',
      admitTime: client.admit_time || '',
      roomNumber: client.room_number || '',
      notes: client.notes || ''
    });
    setEditModal(client);
  };

  const submitEdit = async () => {
    if (!editForm.firstName.trim() || !editForm.lastName.trim()) { alert('First and last name are required.'); return; }
    setSubmittingEdit(true);
    try {
      await api.patch(`/clients/${editModal.id}`, {
        firstName: editForm.firstName.trim(),
        lastName: editForm.lastName.trim(),
        dateOfBirth: editForm.dateOfBirth || null,
        admitDate: editForm.admitDate,
        admitTime: editForm.admitTime || null,
        roomNumber: editForm.roomNumber.trim() || null,
        notes: editForm.notes.trim() || null
      });
      setEditModal(null);
      load();
    } catch (err) {
      alert(err?.response?.data?.error || 'Failed to update client.');
    } finally { setSubmittingEdit(false); }
  };
  const submitDischarge = async () => {
    if (dischargeReason.trim().length < 10) { alert('Please provide a clinical reason (at least 10 characters).'); return; }
    if (isSupervisor && !dischargeType) { alert('Select a discharge type.'); return; }
    // Validate discharge time if provided
    let dischargeDatetime = null;
    if (dischargeTime) {
      const parsed = new Date(dischargeTime);
      const now = new Date();
      if (parsed > now) { alert('Discharge time cannot be in the future.'); return; }
      if ((now - parsed) > 24 * 60 * 60 * 1000) { alert('Discharge time cannot be more than 24 hours in the past.'); return; }
      dischargeDatetime = parsed.toISOString();
    }
    setSubmittingDischarge(true);
    try {
      if (isSupervisor) {
        await api.patch(`/clients/${dischargeModal.id}/discharge`, { reason: dischargeReason, dischargeDatetime, dischargeType });
        setDischargeModal(null); setDischargeReason(''); setDischargeTime(''); setDischargeTime('');
        load();
      } else {
        await api.post('/discharge-requests', { clientId: dischargeModal.id, reason: dischargeReason, dischargeDatetime });
        setDischargeModal(null); setDischargeReason(''); setDischargeTime(''); setDischargeTime('');
        alert('Discharge request submitted. A supervisor will review and approve.');
      }
    } catch (err) {
      alert(err?.response?.data?.error || 'Failed to discharge client.');
    } finally { setSubmittingDischarge(false); }
  };

  const discharge = async (client) => {
    openDischarge(client);
    return; // unused continuation for syntax compatibility below
    if (!isSupervisor) { alert('Only supervisors and admins can discharge clients.'); return; }
    if (!window.confirm(`Discharge ${client.first_name} ${client.last_name}?\n\nTheir check history will be preserved and they can be readmitted within 72 hours.`)) return;
    try { await api.patch(`/clients/${client.id}/discharge`); load(); }
    catch (err) { alert(getErrorMessage(err)); }
  };

  const readmit = async (client) => {
    if (!isSupervisor) { alert('Only supervisors and admins can readmit clients.'); return; }
    const dischargedAt = new Date(client.discharge_date);
    const hoursSince = (Date.now() - dischargedAt.getTime()) / 3600000;
    if (hoursSince > 72) { alert(`${client.first_name} ${client.last_name} was discharged more than 72 hours ago and cannot be automatically readmitted.`); return; }
    if (!window.confirm(`Readmit ${client.first_name} ${client.last_name}?`)) return;
    try { await api.patch(`/clients/${client.id}/readmit`); load(); }
    catch (err) { alert(getErrorMessage(err)); }
  };

  const canReadmit = (client) => {
    if (!client.discharge_date) return false;
    return (Date.now() - new Date(client.discharge_date).getTime()) / 3600000 <= 72;
  };

  const hoursUntilExpiry = (client) => {
    if (!client.discharge_date) return 0;
    return Math.max(0, Math.round(72 - (Date.now() - new Date(client.discharge_date).getTime()) / 3600000));
  };

  const addAction = <button className="btn btn-primary btn-sm" onClick={() => setShowAdd(true)}>+ Add client</button>;

  return (
    <PageWrapper active="clients" title="Clients" actions={addAction}>

      {loading ? <div className="spinner" /> : clients.length === 0 ? (
        <div className="empty-state">
          No active clients.<br />
          <span style={{ color: 'var(--primary)', cursor: 'pointer', fontWeight: 500 }} onClick={() => setShowAdd(true)}>Add one →</span>
        </div>
      ) : (
        <div className="card-grid">
          {clients.map(client => (
            <div key={client.id} className="card" style={{ marginBottom: 0 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 15 }}>{client.first_name} {client.last_name}</div>
                  <div style={{ fontSize: 12, color: '#9CA3AF', marginTop: 2 }}>
                    DOB: {formatDate(client.date_of_birth)} · Admitted: {formatDate(client.admit_date)}
                    {client.admit_time && ` at ${client.admit_time.slice(0, 5)}`}
                  </div>
                  {client.room_number && <div style={{ fontSize: 12, color: 'var(--primary)', fontWeight: 500, marginTop: 2 }}>Room {client.room_number}</div>}
                  {client.program_type && (
                    <div style={{ marginTop: 4 }}>
                      <span style={{
                        background: client.program_type === 'detox' ? '#FEF3C7' : '#EFF6FF',
                        color: client.program_type === 'detox' ? '#92400E' : '#1E40AF',
                        fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20,
                        border: `1px solid ${client.program_type === 'detox' ? '#FCD34D' : '#BFDBFE'}`
                      }}>
                        {client.program_type === 'detox'
                          ? (client.isInDetoxWindow ? `🔶 Detox · Hour ${client.detoxHoursElapsed} of 72` : '🔶 Detox')
                          : '🏠 Residential'}
                      </span>
                    </div>
                  )}
                </div>
                <span className={intervalBadgeClass(client.current_interval_minutes)}>{intervalLabel(client.current_interval_minutes)}</span>
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {isSupervisor && <button className="btn btn-secondary btn-sm" onClick={() => { setIntervalModal(client); setIntervalForm({ intervalMinutes: client.current_interval_minutes, reason: '' }); }}>Change interval</button>}
                {isSupervisor && <button className="btn btn-secondary btn-sm" onClick={() => openRoomModal(client)}>Room{client.room_number ? ` (${client.room_number})` : ''}</button>}
                {isSupervisor && <button className="btn btn-secondary btn-sm" onClick={() => { setLocModal(client); setLocForm({ programType: client.program_type === 'detox' ? 'residential' : 'detox', reason: '' }); }}>Change LOC</button>}
                <button className="btn btn-primary btn-sm" onClick={() => navigate(`/check/${client.id}`)}>Log check</button>
                {isSupervisor && <button className="btn btn-secondary btn-sm" onClick={() => openEdit(client)}>Edit</button>}
                <button className="btn btn-danger btn-sm" onClick={() => openDischarge(client)}>{isSupervisor ? "Discharge" : "Request discharge"}</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Discharged clients */}
      {isSupervisor && (
        <div style={{ marginTop: 24 }}>
          <button className="btn btn-secondary btn-sm" style={{ width: 'auto', marginBottom: 12 }} onClick={() => setShowDischarged(!showDischarged)}>
            {showDischarged ? 'Hide' : 'Show'} recently discharged ({discharged.filter(canReadmit).length} eligible for readmit)
          </button>
          {showDischarged && discharged.length > 0 && (
            <div className="card-grid">
              {discharged.map(client => (
                <div key={client.id} className="card" style={{ marginBottom: 0, opacity: 0.8 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 15 }}>{client.first_name} {client.last_name}</div>
                      <div style={{ fontSize: 12, color: '#9CA3AF', marginTop: 2 }}>Discharged: {formatDate(client.discharge_date)}</div>
                      {canReadmit(client) && <div style={{ fontSize: 11, color: 'var(--accent-dark)', marginTop: 2 }}>Readmit window: {hoursUntilExpiry(client)}h remaining</div>}
                    </div>
                    <span className="badge" style={{ background: '#F3F4F6', color: '#6B7280' }}>Discharged</span>
                  </div>
                  <div style={{ fontSize: 12, color: '#9CA3AF', marginBottom: 8 }}>
                    Admitted: {formatDate(client.admit_date)}
                    {client.admit_date && client.discharge_date && (() => {
                      const days = Math.round((new Date(client.discharge_date) - new Date(client.admit_date)) / 864e5);
                      // A stay of more than a year is almost always a mistyped
                      // admit date, and it silently inflates occupancy for every
                      // month it spans. Flag it rather than let it sit unnoticed.
                      return days > 365
                        ? <span style={{ color: 'var(--red)', fontWeight: 600 }}> · {days.toLocaleString()} day stay — check this date</span>
                        : <span> · {days} day stay</span>;
                    })()}
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    {isSupervisor && (
                      <button className="btn btn-secondary btn-sm" style={{ flex: 1 }} onClick={() => openEdit(client)}>Edit</button>
                    )}
                    {canReadmit(client) && (
                      <button className="btn btn-primary btn-sm" style={{ flex: 1 }} onClick={() => readmit(client)}>Readmit</button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Add client modal */}
      {showAdd && (
        <div className="modal-overlay" onClick={() => setShowAdd(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxHeight: '90vh', overflowY: 'auto' }}>
            <h2>Add new client</h2>
            <form onSubmit={addClient}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <div className="field"><label>First name</label><input required value={form.firstName} onChange={e => setForm(f => ({ ...f, firstName: e.target.value }))} /></div>
                <div className="field"><label>Last name</label><input required value={form.lastName} onChange={e => setForm(f => ({ ...f, lastName: e.target.value }))} /></div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <div className="field"><label>Date of birth</label><input type="date" required value={form.dateOfBirth} onChange={e => setForm(f => ({ ...f, dateOfBirth: e.target.value }))} /></div>
                <div className="field"><label>Admit date</label><input type="date" required min={daysAgoStr(30)} max={todayStr} value={form.admitDate} onChange={e => setForm(f => ({ ...f, admitDate: e.target.value }))} /><div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 3 }}>Within the last 30 days. Not the date of birth.</div></div>
              </div>
              <div className="field">
                <label>Admit time</label>
                <input type="time" value={form.admitTime} onChange={e => setForm(f => ({ ...f, admitTime: e.target.value }))} />
              </div>

              {/* Program type */}
              <div className="field">
                <label>Program type</label>
                <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                  <button
                    type="button"
                    onClick={() => handleProgramTypeChange('residential')}
                    style={{
                      flex: 1, padding: '10px 0', borderRadius: 8, border: '2px solid',
                      borderColor: form.programType === 'residential' ? 'var(--primary)' : '#E5E7EB',
                      background: form.programType === 'residential' ? 'var(--primary)' : '#fff',
                      color: form.programType === 'residential' ? '#fff' : '#374151',
                      fontWeight: 600, fontSize: 13, cursor: 'pointer'
                    }}
                  >
                    Residential
                  </button>
                  <button
                    type="button"
                    onClick={() => handleProgramTypeChange('detox')}
                    style={{
                      flex: 1, padding: '10px 0', borderRadius: 8, border: '2px solid',
                      borderColor: form.programType === 'detox' ? '#D97706' : '#E5E7EB',
                      background: form.programType === 'detox' ? '#D97706' : '#fff',
                      color: form.programType === 'detox' ? '#fff' : '#374151',
                      fontWeight: 600, fontSize: 13, cursor: 'pointer'
                    }}
                  >
                    🔶 Detox
                  </button>
                </div>
                {form.programType === 'detox' && (
                  <div style={{ marginTop: 6, fontSize: 12, color: '#92400E', background: '#FEF3C7', padding: '6px 10px', borderRadius: 6 }}>
                    Detox clients require Q15 or Q30 checks for the first 72 hours.
                  </div>
                )}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <div className="field">
                  <label>Check interval</label>
                  <select value={form.intervalMinutes} onChange={e => setForm(f => ({ ...f, intervalMinutes: e.target.value }))}>
                    <option value={15}>Q15 — every 15 min</option>
                    <option value={30}>Q30 — every 30 min</option>
                    {form.programType !== 'detox' && <option value={60}>Q60 — every hour</option>}
                    {form.programType !== 'detox' && <option value={180}>Q180 — every 3 hours</option>}
                  </select>
                </div>
                <div className="field">
                  <label>Room / Bed</label>
                  <select value={form.roomNumber} onChange={e => setForm(f => ({ ...f, roomNumber: e.target.value }))}>
                    <option value="">— Select room —</option>
                    {bedOptions(form.roomNumber)}
                  </select>
                </div>
              </div>
              <div className="field"><label>Clinical notes (optional)</label><textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} /></div>
              {error && <div className="alert-bar danger">{error}</div>}
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-primary" type="submit" disabled={saving}>{saving ? 'Saving...' : 'Add client'}</button>
                <button className="btn btn-secondary" type="button" onClick={() => setShowAdd(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Interval modal */}
      {intervalModal && (
        <div className="modal-overlay" onClick={() => setIntervalModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2>Change check interval</h2>
            <p style={{ fontSize: 13, color: '#9CA3AF', marginBottom: 12 }}>{intervalModal.first_name} {intervalModal.last_name} · Currently {intervalLabel(intervalModal.current_interval_minutes)}</p>
            <form onSubmit={changeInterval}>
              <div className="field"><label>New interval</label>
                <select value={intervalForm.intervalMinutes} onChange={e => setIntervalForm(f => ({ ...f, intervalMinutes: e.target.value }))}>
                  <option value={15}>Q15 — every 15 minutes</option>
                  <option value={30}>Q30 — every 30 minutes</option>
                  <option value={60}>Q60 — every hour</option>
                  <option value={180}>Q180 — every 3 hours</option>
                </select>
              </div>
              <div className="field"><label>Clinical reason</label>
                <textarea required value={intervalForm.reason} onChange={e => setIntervalForm(f => ({ ...f, reason: e.target.value }))} placeholder="e.g. Client stabilized, reducing to Q60 per clinical team review" />
              </div>
              {error && <div className="alert-bar danger">{error}</div>}
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-primary" type="submit" disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
                <button className="btn btn-secondary" type="button" onClick={() => setIntervalModal(null)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Room modal */}
      {roomModal && (
        <div className="modal-overlay" onClick={() => setRoomModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2>Room number</h2>
            <p style={{ fontSize: 13, color: '#9CA3AF', marginBottom: 12 }}>{roomModal.first_name} {roomModal.last_name} · Currently: {roomModal.room_number || 'No room assigned'}</p>
            <form onSubmit={changeRoom}>
              <div className="field"><label>Room / Bed</label>
                <select value={roomForm.roomNumber} onChange={e => setRoomForm(f => ({ ...f, roomNumber: e.target.value }))}>
                  <option value="">— Unassigned —</option>
                  {bedOptions(roomForm.roomNumber)}
                </select>
              </div>
              <div className="field"><label>Reason (optional)</label><input value={roomForm.reason} onChange={e => setRoomForm(f => ({ ...f, reason: e.target.value }))} placeholder="e.g. Room transfer, bed reassignment" /></div>
              {error && <div className="alert-bar danger">{error}</div>}
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-primary" type="submit" disabled={saving}>{saving ? 'Saving...' : 'Update room'}</button>
                <button className="btn btn-secondary" type="button" onClick={() => setRoomModal(null)}>Cancel</button>
              </div>
            </form>
            {roomHistory.length > 0 && (
              <div style={{ marginTop: 16 }}>
                <div className="section-label">Room history</div>
                {roomHistory.map(rh => (
                  <div key={rh.id} style={{ fontSize: 12, padding: '6px 0', borderBottom: '1px solid #F3F4F6', color: '#4B5563' }}>
                    <span style={{ color: '#9CA3AF' }}>{new Date(rh.changed_at).toLocaleDateString('en-US')} · </span>
                    {rh.old_room || 'None'} → {rh.new_room || 'None'}
                    <span style={{ color: '#9CA3AF' }}> · {rh.changed_by_name}</span>
                    {rh.reason && <span style={{ color: '#9CA3AF' }}> · {rh.reason}</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}


      {/* LOC change modal */}
      {locModal && (
        <div className="modal-overlay" onClick={() => setLocModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2>Change level of care</h2>
            <p style={{ fontSize: 13, color: '#9CA3AF', marginBottom: 12 }}>
              {locModal.first_name} {locModal.last_name} · Currently: <strong>{locModal.program_type === 'detox' ? 'Detox' : 'Residential'}</strong>
            </p>
            <form onSubmit={changeLoc}>
              <div className="field">
                <label>New level of care</label>
                <select value={locForm.programType} onChange={e => setLocForm(f => ({ ...f, programType: e.target.value }))}>
                  <option value="detox">Detox</option>
                  <option value="residential">Residential</option>
                </select>
              </div>
              <div className="field">
                <label>Clinical reason</label>
                <textarea required value={locForm.reason} onChange={e => setLocForm(f => ({ ...f, reason: e.target.value }))} placeholder="e.g. Client has completed detox protocol, transitioning to residential treatment" />
              </div>
              {error && <div className="alert-bar danger">{error}</div>}
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-primary" type="submit" disabled={saving}>{saving ? 'Saving...' : 'Update level of care'}</button>
                <button className="btn btn-secondary" type="button" onClick={() => setLocModal(null)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}


      {/* Edit client modal */}
      {editModal && (
        <div className="modal-overlay" onClick={() => setEditModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 480 }}>
            <h2>Edit client</h2>
            <p style={{ fontSize: 13, color: '#9CA3AF', marginBottom: 12 }}>
              {editModal.first_name} {editModal.last_name}
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div className="field"><label>First name</label><input type="text" value={editForm.firstName} onChange={e => setEditForm(f => ({ ...f, firstName: e.target.value }))} /></div>
              <div className="field"><label>Last name</label><input type="text" value={editForm.lastName} onChange={e => setEditForm(f => ({ ...f, lastName: e.target.value }))} /></div>
              <div className="field"><label>Date of birth</label><input type="date" value={editForm.dateOfBirth} onChange={e => setEditForm(f => ({ ...f, dateOfBirth: e.target.value }))} /></div>
              <div className="field"><label>Room number</label><input type="text" value={editForm.roomNumber} onChange={e => setEditForm(f => ({ ...f, roomNumber: e.target.value }))} /></div>
              <div className="field"><label>Admit date</label><input type="date" min={daysAgoStr(730)} max={todayStr} value={editForm.admitDate} onChange={e => setEditForm(f => ({ ...f, admitDate: e.target.value }))} /></div>
              <div className="field"><label>Admit time</label><input type="time" value={editForm.admitTime} onChange={e => setEditForm(f => ({ ...f, admitTime: e.target.value }))} /></div>
            </div>
            <div className="field" style={{ marginTop: 4 }}>
              <label>Notes</label>
              <textarea value={editForm.notes} onChange={e => setEditForm(f => ({ ...f, notes: e.target.value }))} placeholder="Optional clinical notes..." style={{ minHeight: 60 }} />
            </div>
            <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 4, marginBottom: 10 }}>
              Note: To change interval or level of care, use the dedicated buttons on the client card.
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-primary" onClick={submitEdit} disabled={submittingEdit}>{submittingEdit ? 'Saving...' : 'Save changes'}</button>
              <button className="btn btn-secondary" type="button" onClick={() => setEditModal(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Discharge request modal */}
      {dischargeModal && (
        <div className="modal-overlay" onClick={() => { setDischargeModal(null); setDischargeReason(''); setDischargeTime(''); }}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2>{isSupervisor ? "Discharge client" : "Request discharge"}</h2>
            <p style={{ fontSize: 13, color: '#9CA3AF', marginBottom: 12 }}>
              {dischargeModal.first_name} {dischargeModal.last_name}{dischargeModal.room_number ? ` · Room ${dischargeModal.room_number}` : ''}
            </p>
            {!isSupervisor && (
              <div className="alert-bar info" style={{ marginBottom: 14, background: '#EAF0FB', color: '#1E4080', padding: '10px 12px', borderRadius: 6, fontSize: 13 }}>
                This will be sent to a supervisor for approval. The client will stay active until approved.
              </div>
            )}
            {isSupervisor && (
              <div className="alert-bar warning" style={{ marginBottom: 14, background: '#FFF3E0', color: '#92400E', padding: '10px 12px', borderRadius: 6, fontSize: 13 }}>
                ⚠ This will immediately discharge the client from active census. The check history is preserved and they can be readmitted within 72 hours.
              </div>
            )}
            <div className="field">
              <label>Clinical reason for discharge</label>
              <textarea required value={dischargeReason} onChange={e => setDischargeReason(e.target.value)} placeholder="Describe the reason for discharge (minimum 10 characters)..." style={{ minHeight: 90 }} />
            </div>

            {isSupervisor && (
              <div className="field">
                <label>Discharge type <span style={{ color: 'var(--red)' }}>· required</span></label>
                <select value={dischargeType} onChange={e => setDischargeType(e.target.value)}>
                  <option value="">Select type…</option>
                  {dischargeTypes.map(t => <option key={t.code} value={t.code}>{t.label}</option>)}
                </select>
                {dischargeType && dischargeTypes.find(t => t.code === dischargeType)?.description && (
                  <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 3 }}>
                    {dischargeTypes.find(t => t.code === dischargeType).description}
                  </div>
                )}
              </div>
            )}

            <div className="field">
              <label>Discharge time <span style={{ color: '#9CA3AF', fontWeight: 400 }}>(optional — leave blank for now)</span></label>
              <input type="datetime-local" value={dischargeTime} onChange={e => setDischargeTime(e.target.value)} max={new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16)} />
              <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 4 }}>
                If the client left earlier today, set the actual departure time. Any missed checks scheduled after this time will be cleared.
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-primary" onClick={submitDischarge} disabled={submittingDischarge || dischargeReason.trim().length < 10 || (isSupervisor && !dischargeType)}>
                {submittingDischarge ? (isSupervisor ? "Discharging..." : "Submitting...") : (isSupervisor ? "Discharge client" : "Submit for approval")}
              </button>
              <button className="btn btn-secondary" type="button" onClick={() => { setDischargeModal(null); setDischargeReason(''); setDischargeTime(''); }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

    </PageWrapper>
  );
}
